import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, MapPin, Phone, Send, Instagram, Youtube, Facebook, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiry_type: 'general',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus({ 
          type: 'success', 
          message: t('contact.successMessage') 
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          inquiry_type: 'general',
          subject: '',
          message: ''
        });
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      setStatus({ 
        type: 'error', 
        message: t('contact.errorMessage')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const inquiryTypes = [
    { value: 'general', label: t('contact.general') },
    { value: 'retreat', label: t('contact.retreat') },
    { value: 'speaking', label: t('contact.speaking') },
    { value: 'media', label: t('contact.media') },
    { value: 'dxn_business', label: t('contact.dxnBusiness') },
    { value: 'other', label: t('contact.other') }
  ];

  return (
    <div className="min-h-screen pt-20">
      <SEOHead 
        title={t('contact.title')}
        description="Contact Dr. Lim Siow Jin's team for Sunya retreat information, speaking engagements, media inquiries, DXN business, or general questions."
        keywords="contact Dr. Lim Siow Jin, Sunya retreat registration, speaking engagement, media inquiry"
      />
      
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('contact.title')}</h1>
          <p className="text-xl text-emerald-100 max-w-3xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('contact.send')}</h2>
              
              {/* Status Message */}
              {status.message && (
                <div className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
                  status.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}>
                  {status.type === 'success' ? (
                    <CheckCircle size={20} />
                  ) : (
                    <AlertCircle size={20} />
                  )}
                  <span>{status.message}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('contact.name')} *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="John Doe"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('contact.email')} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="john@example.com"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('contact.phone')}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="+60 12-345 6789"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label htmlFor="inquiry_type" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('contact.inquiryType')} *
                    </label>
                    <select
                      id="inquiry_type"
                      name="inquiry_type"
                      value={formData.inquiry_type}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      data-testid="contact-inquiry-type"
                    >
                      {inquiryTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('contact.subject')} *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Subject of your inquiry"
                    data-testid="contact-subject"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('contact.message')} *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                    placeholder="Please share your message or inquiry..."
                    data-testid="contact-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full md:w-auto px-8 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-semibold flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader size={18} className="animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <span>{t('contact.send')}</span>
                      <Send size={18} />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            {/* Contact Details */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-200">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Contact Information</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Address</h4>
                    <p className="text-sm text-gray-600">
                      DXN Cyberville<br />
                      Cyberjaya, Selangor<br />
                      Malaysia
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                    <Phone className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                    <p className="text-sm text-gray-600">+603-8680 9861</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0">
                    <Mail className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                    <p className="text-sm text-gray-600">contact@drlimsiowjin.com</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Follow on Social Media</h3>
              <div className="space-y-3">
                <a
                  href="https://instagram.com/limsiowjin"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 transition-all group"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                    <Instagram className="text-white" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 group-hover:text-purple-700">Instagram</p>
                    <p className="text-xs text-gray-500">@limsiowjin</p>
                  </div>
                </a>
                <a
                  href="https://www.youtube.com/@SunyaStudios"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 p-3 rounded-lg hover:bg-red-50 transition-all group"
                >
                  <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center">
                    <Youtube className="text-white" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 group-hover:text-red-700">YouTube</p>
                    <p className="text-xs text-gray-500">Sunya Studios</p>
                  </div>
                </a>
                <a
                  href="https://www.facebook.com/Dr.Rajesh.Savera"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-all group"
                >
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                    <Facebook className="text-white" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 group-hover:text-blue-700">Facebook</p>
                    <p className="text-xs text-gray-500">Dr. Rajesh Savera</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Official Websites */}
            <div className="bg-gradient-to-br from-gray-50 to-slate-100 rounded-2xl p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Official Websites</h3>
              <div className="space-y-2">
                <a href="https://drlimsiowjin.com" target="_blank" rel="noopener noreferrer" className="block text-emerald-700 hover:text-emerald-800 font-medium text-sm">
                  → drlimsiowjin.com
                </a>
                <a href="https://sifworld.com" target="_blank" rel="noopener noreferrer" className="block text-emerald-700 hover:text-emerald-800 font-medium text-sm">
                  → sifworld.com (Sunyatee Foundation)
                </a>
                <a href="https://sunyatimes.com" target="_blank" rel="noopener noreferrer" className="block text-emerald-700 hover:text-emerald-800 font-medium text-sm">
                  → sunyatimes.com (News)
                </a>
                <a href="https://sunyatee.com" target="_blank" rel="noopener noreferrer" className="block text-emerald-700 hover:text-emerald-800 font-medium text-sm">
                  → sunyatee.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
